import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Menu, X } from 'lucide-react';
import { DocCategory, DocItem, DocSearchResult } from '../../types/help';
import docsService from '../../services/docsService';
import docsSearchService from '../../services/docsSearchService';
import DocNavigation from '../help/DocNavigation';
import DocContent from '../help/DocContent';
import SearchBar from '../help/SearchBar';

const BootcampDocumentation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [docNavigation, setDocNavigation] = useState<DocCategory[]>([]);
  const [currentDocPath, setCurrentDocPath] = useState<string | null>(
    searchParams.get('doc') || null
  );
  const [docContent, setDocContent] = useState<string | null>(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [docSearchResults, setDocSearchResults] = useState<DocSearchResult[]>([]);
  const [isSearchingDocs, setIsSearchingDocs] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const loadDocs = async () => {
      try {
        const nav = await docsService.loadNavigation();
        setDocNavigation(nav);
        // Only default to first doc if no URL param was provided
        if (!currentDocPath && nav.length > 0 && nav[0].items.length > 0) {
          const defaultPath = nav[0].items[0].path;
          setCurrentDocPath(defaultPath);
          setSearchParams({ doc: defaultPath }, { replace: true });
        }
      } catch (error) {
        console.error('Failed to load documentation navigation:', error);
      }
      try {
        await docsSearchService.initialize();
      } catch (error) {
        console.error('Failed to initialize doc search:', error);
      }
    };
    loadDocs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentDocPath) return;
    const loadContent = async () => {
      setIsLoadingDoc(true);
      setDocError(null);
      try {
        const content = await docsService.loadDocument(currentDocPath);
        setDocContent(content);
      } catch (error) {
        console.error('Failed to load document:', error);
        setDocContent(null);
        setDocError('The requested document could not be loaded. Please try selecting another topic.');
      } finally {
        setIsLoadingDoc(false);
      }
    };
    loadContent();
  }, [currentDocPath]);

  const handleDocSearch = useCallback((query: string) => {
    if (!query.trim()) {
      setDocSearchResults([]);
      setIsSearchingDocs(false);
      return;
    }
    setIsSearchingDocs(true);
    try {
      const results = docsSearchService.search(query);
      setDocSearchResults(results);
    } catch (error) {
      console.error('Doc search failed:', error);
      setDocSearchResults([]);
    } finally {
      setIsSearchingDocs(false);
    }
  }, []);

  const handleDocSelect = useCallback((path: string) => {
    setCurrentDocPath(path);
    setSearchParams({ doc: path }, { replace: true });
    setIsMobileSidebarOpen(false);
  }, [setSearchParams]);

  const handleDocResultSelect = useCallback((path: string) => {
    handleDocSelect(path);
    setDocSearchResults([]);
  }, [handleDocSelect]);

  // Compute flat list of all doc items for prev/next navigation and breadcrumb
  const flatItems = useMemo(() => {
    const items: { item: DocItem; categoryTitle: string }[] = [];
    docNavigation.forEach((cat) => {
      cat.items.forEach((item) => {
        items.push({ item, categoryTitle: cat.title });
      });
    });
    return items;
  }, [docNavigation]);

  const currentIndex = useMemo(
    () => flatItems.findIndex((entry) => entry.item.path === currentDocPath),
    [flatItems, currentDocPath]
  );

  const prevArticle = currentIndex > 0 ? flatItems[currentIndex - 1] : null;
  const nextArticle = currentIndex >= 0 && currentIndex < flatItems.length - 1 ? flatItems[currentIndex + 1] : null;
  const currentArticle = currentIndex >= 0 ? flatItems[currentIndex] : null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => navigate('/bootcamp')}
          className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-warm-white transition-colors mb-3"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Bootcamp
        </button>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-warm-white mb-3">
          Documentation
        </h1>
        <SearchBar
          onSearch={handleDocSearch}
          onResultSelect={handleDocResultSelect}
          results={docSearchResults}
          isSearching={isSearchingDocs}
          placeholder="Search documentation..."
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile sidebar toggle */}
        <button
          type="button"
          onClick={() => setIsMobileSidebarOpen(true)}
          className="md:hidden absolute top-2 left-2 z-10 p-2 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-warm-white hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Open navigation"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Mobile sidebar overlay */}
        {isMobileSidebarOpen && (
          <div
            className="md:hidden fixed inset-0 z-30 bg-black/50"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Navigation sidebar */}
        <div
          className={`
            fixed inset-y-0 left-0 z-40 w-72 bg-white dark:bg-cap-dark border-r border-gray-200 dark:border-gray-700 overflow-y-auto transform transition-transform duration-200 ease-in-out
            md:static md:w-72 md:flex-shrink-0 md:translate-x-0 md:z-auto
            ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          {/* Mobile close button */}
          <div className="md:hidden flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 pl-2">Navigation</span>
            <button
              type="button"
              onClick={() => setIsMobileSidebarOpen(false)}
              className="p-1 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-warm-white"
              aria-label="Close navigation"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <DocNavigation
            categories={docNavigation}
            currentPath={currentDocPath}
            onSelect={handleDocSelect}
            isLoading={false}
          />
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pl-12 md:pl-4">
          {/* Breadcrumb */}
          {currentArticle && !isLoadingDoc && (
            <nav aria-label="Breadcrumb" className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              <span>{currentArticle.categoryTitle}</span>
              <span className="mx-2">/</span>
              <span className="text-gray-900 dark:text-warm-white font-medium">{currentArticle.item.title}</span>
            </nav>
          )}

          <DocContent
            content={docContent}
            isLoading={isLoadingDoc}
            error={docError}
            onLinkClick={handleDocSelect}
          />

          {/* Prev/Next Navigation */}
          {currentArticle && !isLoadingDoc && !docError && docContent && (
            <nav aria-label="Article navigation" className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              {prevArticle ? (
                <button
                  type="button"
                  onClick={() => handleDocSelect(prevArticle.item.path)}
                  className="flex flex-col items-start text-sm hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <span className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Previous</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">&larr; {prevArticle.item.title}</span>
                </button>
              ) : <div />}
              {nextArticle ? (
                <button
                  type="button"
                  onClick={() => handleDocSelect(nextArticle.item.path)}
                  className="flex flex-col items-end text-sm hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <span className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Next</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{nextArticle.item.title} &rarr;</span>
                </button>
              ) : <div />}
            </nav>
          )}
        </div>
      </div>
    </div>
  );
};

export default BootcampDocumentation;
